import React, {
    createContext,
    useState,
    useEffect,
    useCallback,
    useContext,
    ReactNode,
} from "react";
import api from "../../api/api"; // Your Axios instance
import { PaginationInfo } from "../../types/api";

interface ToolContextType {
    tools: any[];
    pagination: PaginationInfo | null;
    loading: boolean;
    fetchTools: (page?: number, perPage?: number) => Promise<void>;
    addTool: (tool: any) => Promise<void>;
    editTool: (updatedTool: any) => Promise<void>;
    deleteTool: (id: number | string) => Promise<void>;
}

export const ToolContext = createContext<ToolContextType | undefined>(
    undefined,
);

interface ToolProviderProps {
    children: ReactNode;
}

export const useToolContext = (): ToolContextType => {
    const context = useContext(ToolContext);
    if (!context) {
        throw new Error("useToolContext must be used within ToolProvider");
    }
    return context;
};

const ToolProvider: React.FC<ToolProviderProps> = ({ children }) => {
    const [tools, setTools] = useState<any[]>([]);
    const [pagination, setPagination] = useState<PaginationInfo | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    // Fetch tools from API with pagination
    const fetchTools = useCallback(async (page: number = 1, perPage: number = 15) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                per_page: perPage.toString(),
            });
            const response = await api.get(`/tools?${params.toString()}`);
            
            // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
            let toolsData = [];
            let paginationData = null;
            const responseData = response.data;
            
            if (responseData?.success) {
                if (responseData.data?.data && Array.isArray(responseData.data.data)) {
                    toolsData = responseData.data.data;
                    paginationData = responseData.data.pagination || responseData.pagination;
                } else if (responseData.data && Array.isArray(responseData.data)) {
                    toolsData = responseData.data;
                    paginationData = responseData.pagination;
                } else if (Array.isArray(responseData)) {
                    toolsData = responseData;
                }
            } else if (Array.isArray(responseData?.data)) {
                toolsData = responseData.data;
            } else if (Array.isArray(responseData)) {
                toolsData = responseData;
            }
            
            setTools(toolsData);
            setPagination(paginationData);
        } catch (error) {
            console.error("Error fetching tools:", error);
            setTools([]);
            setPagination(null);
        } finally {
            setLoading(false);
        }
    }, []);

    // Add tool
    const addTool = async (tool: any): Promise<void> => {
        try {
            const response = await api.post("/tools", tool);
            const newTool = response.data.data || response.data;
            setTools((prev) => [...prev, newTool]);
        } catch (error) {
            if (error.response?.data?.errors) {
                throw error.response.data.errors; // Throw server-side validation errors
            }
            throw error;
        }
    };

    const editTool = async (updatedTool: any): Promise<void> => {
        try {
            const response = await api.put(`/tools/${updatedTool.id}`, updatedTool);
            const updatedToolData = response.data.data || response.data;
            setTools((prev) =>
                prev.map((tool) => (tool.id === updatedTool.id ? updatedToolData : tool))
            );
        } catch (error) {
            if (error.response?.data?.errors) {
                throw error.response.data.errors; // Throw server-side validation errors
            }
            throw error;
        }
    };


    // Delete tool
    const deleteTool = async (id: number | string): Promise<void> => {
        try {
            await api.delete(`/tools/${id}`);
            setTools((prev) => prev.filter((tool) => tool.id !== id));
        } catch (error) {
            console.error("Error deleting tool:", error);
        }
    };

    // Remove auto-fetch on mount to prevent infinite loops
    // Data will be fetched when explicitly called from components with pagination

    return (
        <ToolContext.Provider value={{ tools, pagination, loading, fetchTools, addTool, editTool, deleteTool }}>
            {children}
        </ToolContext.Provider>
    );
};

export default ToolProvider;
